import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { maintenanceAPI } from '../utils/api';
import { MaintenanceTemplate } from '../types';
import { Plus, Edit, Trash2, Settings, ArrowLeft, Calendar, Clock } from 'lucide-react';
import { useNotifications } from '../utils/notifications';

const MaintenanceTemplatesList: React.FC = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const [templates, setTemplates] = useState<MaintenanceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadTemplates();
  }, [filter]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filter !== 'all') {
        params.is_active = filter === 'active';
      }
      const response = await maintenanceAPI.getTemplates(params);
      setTemplates(response.templates);
    } catch (error: any) {
      console.error('Error loading templates:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load templates',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (template: MaintenanceTemplate) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await maintenanceAPI.deleteTemplate(template.id);
      addNotification({
        type: 'success',
        message: 'Template deleted successfully',
      });
      loadTemplates();
    } catch (error: any) {
      addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to delete template',
      });
    }
  };

  const getSchedulingInfo = (template: MaintenanceTemplate) => {
    const parts: string[] = [];
    
    if (template.scheduling_mode === 'duration' || template.scheduling_mode === 'whichever_first') {
      if (template.duration_months) {
        parts.push(`${template.duration_months} month${template.duration_months > 1 ? 's' : ''}`);
      }
      if (template.duration_days) {
        parts.push(`${template.duration_days} day${template.duration_days > 1 ? 's' : ''}`);
      }
    }
    
    if (template.scheduling_mode === 'run_hours' || template.scheduling_mode === 'whichever_first') {
      if (template.run_hours_interval) {
        parts.push(`${template.run_hours_interval}h`);
      }
    }
    
    return parts.join(' or ') || 'Not configured';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/maintenance')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Maintenance Templates</h1>
                <p className="text-sm text-gray-600 mt-1">Create and manage reusable maintenance templates</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/maintenance/templates/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Template
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'active' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilter('inactive')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filter === 'inactive' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Inactive
            </button>
          </div>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Settings className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-600 mb-4">Create your first maintenance template to get started.</p>
            <button
              onClick={() => navigate('/maintenance/templates/new')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{template.name}</h3>
                    <p className="text-sm text-gray-600">{template.description || 'No description'}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>{getSchedulingInfo(template)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>Alert: {template.alert_days_before} days / {template.alert_hours_before}h before</span>
                  </div>
                  {template.tasks && template.tasks.length > 0 && (
                    <div className="text-sm text-gray-600">
                      {template.tasks.length} task{template.tasks.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <button
                    onClick={() => navigate(`/maintenance/templates/${template.id}`)}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(template)}
                    className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceTemplatesList;

